import { Injectable } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../data-access/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class BaseApiService {
  constructor(private http: HttpClient) {
  }

  private formatErrors(error: any) {
    return throwError(error?.error || error);
  }

  private checkHeaders(headers?: HttpHeaders): HttpHeaders {
    if (!headers) {
      headers = new HttpHeaders().set('Content-Type', 'application/json');
    }

    const headerKeys = headers.keys();

    if (
      headerKeys.length > 0 &&
      (headerKeys.indexOf('content-type') ||
        headerKeys.indexOf('Content-Type')) < 0
    ) {
      headers = headers.set('Content-Type', 'application/json');
    }

    return headers;
  }

  private checkPath(path: string) {
    if (path[0] === '/') {
      // remove leading slash form the path
      path = path.substr(1);
    }

    return path;
  }

  getAbsoluteUrl(url: string) {
    if (url.substr(0, 7) === 'http://' || url.substr(0, 7) === 'https://') {
      return url;
    }
    return `${environment.baseApiUrl}${url}`;
  }

  getApiPath(relativePath: any) {
    return `${environment.baseApiUrl}${relativePath}`;
  }

  get(
    path: string,
    params: HttpParams = new HttpParams(),
    headers?: HttpHeaders,
  ): Observable<any> {
    path = this.checkPath(path);
    headers = this.checkHeaders(headers);

    return this.http
      .get(`${environment.baseApiUrl}${path}`, {
        params,
        headers,
      })
      .pipe(catchError(this.formatErrors));
  }

  getDPX(
    path: string,
    params: HttpParams = new HttpParams(),
    headers?: HttpHeaders,
  ): Observable<any> {
    path = this.checkPath(path);
    headers = this.checkHeaders(headers);

    return this.http
      .get(`digipay/api/${path}`, {
        params,
        headers,
      })
      .pipe(catchError(this.formatErrors));
  }

  getWealth(
    path: string,
    params: HttpParams = new HttpParams(),
  ): Observable<any> {
    path = this.checkPath(path);

    return this.http
      .get(`/api/${path}`, {
        params,
      })
      .pipe(catchError(this.formatErrors));
  }

  getFile(path: string): Observable<any> {
    path = this.checkPath(path);
    return this.http
      .get(`${environment.baseApiUrl}/${path}`, {
        responseType: 'arraybuffer',
      })
      .pipe(catchError(this.formatErrors));
  }

  getCreatePayment(
    path: string,
    params: HttpParams = new HttpParams(),
  ): Observable<any> {
    path = this.checkPath(path);
    const base = environment.baseApiUrl.split('/')[2];
    return this.http
      .get(`${base}/${path}`, {
        params,
      })
      .pipe(catchError(this.formatErrors));
  }

  put(path: string, body: Object = {}): Observable<any> {
    path = this.checkPath(path);

    return this.http
      .put(`${environment.baseApiUrl}${path}`, JSON.stringify(body), {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .pipe(catchError(this.formatErrors));
  }

  putWealth(path: string, body: Object = {}): Observable<any> {
    path = this.checkPath(path);

    return this.http
      .put(`/api/${path}`, JSON.stringify(body), {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .pipe(catchError(this.formatErrors));
  }

  post(
    path: string,
    body: Object = {},
    headers?: HttpHeaders,
  ): Observable<any> {
    headers = this.checkHeaders(headers);

    path = this.checkPath(path);
    return this.http
      .post(environment.baseApiUrl + `${path}`, JSON.stringify(body), {
        headers: headers,
      })
      .pipe(catchError(this.formatErrors));
  }

  postWealth(
    path: string,
    body: Object = {},
    headers?: HttpHeaders,
  ): Observable<any> {
    headers = this.checkHeaders(headers);

    path = this.checkPath(path);
    return this.http
      .post('/api/' + `${path}`, JSON.stringify(body), {
        headers: headers,
      })
      .pipe(catchError(this.formatErrors));
  }

  postAuth(path: string, body: Object = {}): Observable<any> {
    path = this.checkPath(path);
    const base = environment.baseApiUrl.split('/wealth/')[0];
    return this.http
      .post(`${base}/${path}`, JSON.stringify(body))
      .pipe(catchError(this.formatErrors));
  }

  postBase64(
    path: string,
    body: Object = {},
    headers?: HttpHeaders,
  ): Observable<any> {
    return this.http
      .post(`${environment.baseApiUrl}${path}`, JSON.stringify(body), {
        responseType: 'blob' as 'json',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .pipe(catchError(this.formatErrors));
  }

  delete(
    path: any,
    option?: {
      headers?: HttpHeaders;
      params?: HttpParams;
      body?: any | null;
    },
  ): Observable<any> {
    return this.http
      .delete(`${environment.baseApiUrl}${path}`, option)
      .pipe(catchError(this.formatErrors));
  }

  getImage(id: string): Observable<any> {
    return this.http
      .get(`${environment.baseApiUrl}files/${id}`, {responseType: 'blob'})
      .pipe(catchError(this.formatErrors));
  }

  getCreditImage(id: string): Observable<any> {
    // id should contain minio's bucket name too.
    // example: activation/qwe0230-139392kwe.png
    return this.http
      .get(`${environment.baseApiUrl}contents/${id}`, {responseType: 'blob'})
      .pipe(catchError(this.formatErrors));
  }

  getCertByUrl(certFileUrl: string): Observable<any> {
    const url = certFileUrl.split('/digipay/api/').pop();
    return this.http
      .get(`${environment.baseApiUrl}${url}`, {
        responseType: 'text',
        headers: {
          // 'Content-Type': 'text/html'
        },
      })
      .pipe(catchError(this.formatErrors));
  }

  getCertFile(fileId: string): Observable<any> {
    return this.http
      .get(`${environment.baseApiUrl}certs/${fileId}`, {
        responseType: 'text',
        headers: {
          // 'Content-Type': 'text/html'
        },
      })
      .pipe(catchError(this.formatErrors));
  }

  getPublicKey(certName: string): Observable<any> {
    return this.http.get(`${certName}`, {responseType: 'text'});
  }

  fileUrl(fileId: any) {
    return `${environment.baseApiUrl}files/${fileId}`;
  }

  getPage(pageFileId: any) {
    return this.http.get(this.fileUrl(pageFileId), {
      responseType: 'text',
      headers: {
        Accept: 'text/html,application/xhtml+xml;',
      },
    });
  }

  /**
   * Get HTML content
   *
   * @param relativeUrl
   */
  getHtml(relativeUrl: string) {
    relativeUrl = this.checkPath(relativeUrl);
    const absoluteUrl = this.getAbsoluteUrl(relativeUrl);
    return this.http.get(absoluteUrl, {
      responseType: 'text',
      headers: {
        Accept: 'text/html,application/xhtml+xml;',
      },
    });
  }

  /**
   *
   * @param path
   * @param formData
   */
  multiPartUpload(path: string, formData: FormData): Observable<any> {
    return this.http.post(`${environment.baseApiUrl}${path}`, formData, {
      headers: {
        // 'Content-Type': 'multipart/form-data'
        // 'Content-Type': undefined,
        'ngsw-bypass': '1',
      },
    });
  }

  /**
   *
   * @param fileId
   */
  deleteFile(fileId: any): Observable<any> {
    return this.http.delete(`${environment.baseApiUrl}files/${fileId}`);
  }

  getParameterByName(name: string, url: string): any {
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  /**
   * Convert the given absolute URL to a relative URL
   *
   * Sometimes URL is an absolute full url (like http://api.mydigipay.com/digipay/api/what/ever)
   * and we need to convert it to a relative URL, in this case we can use this helper method.
   *
   * @param fullAbsoluteUrl
   */
  static convertToRelativeUrl(fullAbsoluteUrl: string) {
    return fullAbsoluteUrl.substr(fullAbsoluteUrl.lastIndexOf('/api') + 5);
  }
}
